import { CSSProperties } from 'react';
import Select, { StylesConfig } from 'react-select';
import chroma from 'chroma-js';
import type { Option, GroupedOption } from '../types';
import { datoWarningYellow, datoFocusRed, datoNoticeGreen, datoLightBg } from '../utils/consts';

interface OptionStyleConf {
  data: Option;
  isFocused: boolean;
  isSelected: boolean;
}

interface CustomSelectProps {
  groupedOptions: GroupedOption[];
  setValue: (args: any) => void;
  value: Option | null;
}

const dot = (color = 'transparent') => ({
  alignItems: 'center',
  display: 'flex',

  ':before': {
    backgroundColor: color,
    borderRadius: 10,
    content: '" "',
    display: 'block',
    marginRight: 8,
    height: 10,
    width: 10,
  },
});


const colourStyles: StylesConfig<Option> = {
  control: (styles: { [x: string]: any; }) => 
    ({
      ...styles,
      backgroundColor: 'white',
    }),
  option: (styles: { [x: string]: any; }, { data, isFocused }: OptionStyleConf) => {
    const color = chroma(data.env === 'prod' ? datoNoticeGreen : datoWarningYellow);
    return {
      ...styles,
      backgroundColor: isFocused ? datoLightBg : undefined,
      color: 'black',

      ':active': {
        ...styles[':active'],
        backgroundColor: color.alpha(0.3).css()
      },
      ...dot(color.css())
    };
  },
  // @ts-ignore
  input: (styles) => ({ ...styles, ...dot() }),
  // @ts-ignore
  placeholder: (styles) => ({ ...styles, ...dot('#ccc') }),
  // @ts-ignore
  singleValue: (styles, { data }) => ({ ...styles, ...dot(data.env === 'prod' ? datoNoticeGreen : datoWarningYellow) }),
};

const groupStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};
const groupBadgeStyles: CSSProperties = {
  backgroundColor: '#EBECF0',
  borderRadius: '2em',
  color: '#172B4D',
  display: 'inline-block',
  fontSize: 12,
  fontWeight: 'normal',
  lineHeight: '1',
  minWidth: 1,
  padding: '0.16666666666667em 0.5em',
  textAlign: 'center',
};

const formatGroupLabel = (data: GroupedOption) => (
  <div style={groupStyles}>
    <span>{data.label}</span>
    <span style={groupBadgeStyles}>{data.options.length}</span>
  </div>
);

export const CustomSelect = ({ groupedOptions, setValue, value }: CustomSelectProps) => (
  <Select<Option, false, GroupedOption>
    value={value}
    options={groupedOptions}
    formatGroupLabel={formatGroupLabel}
    isClearable
    onChange={setValue}
    // @ts-ignore
    styles={colourStyles}
    theme={(theme) => ({
      ...theme,
      borderRadius: 0,
      colors: {
        ...theme.colors,
        primary: datoFocusRed,
      },
    })}
  />
);

export default CustomSelect;